import React, { useEffect, useState } from 'react';
import { useAuth } from '../features/auth/useAuth';

interface SessionTimeoutWarningProps {
  onExtendSession?: () => void;
}

export const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({ 
  onExtendSession 
}) => {
  const { timeUntilExpiration, refreshUserData, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (timeUntilExpiration > 0) {
      // Show warning when less than 30 minutes remaining
      const thirtyMinutes = 30 * 60 * 1000; // 30 minutes in milliseconds
      if (timeUntilExpiration <= thirtyMinutes) {
        setShowWarning(true);
        setTimeLeft(timeUntilExpiration);
      } else {
        setShowWarning(false);
      }
    } else {
      setShowWarning(false);
    }
  }, [timeUntilExpiration]);

  useEffect(() => {
    if (!showWarning) return;

    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 0) {
          logout();
          return 0;
        }
        return prev - 1000; // Update every second
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showWarning, logout]);

  const handleExtendSession = async () => {
    try {
      await refreshUserData();
      setShowWarning(false);
      if (onExtendSession) {
        onExtendSession();
      }
    } catch (error) {
      console.error('Failed to extend session:', error);
    }
  };

  const handleLogout = () => {
    logout();
    setShowWarning(false);
  };

  const formatTime = (milliseconds: number): string => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!showWarning) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">
                Session Timeout Warning
              </h3>
            </div>
          </div>
          
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Your session will expire in <span className="font-semibold text-red-600">
                {formatTime(timeLeft)}
              </span>. Would you like to extend your session?
            </p>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={handleLogout}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Logout Now
            </button>
            <button
              onClick={handleExtendSession}
              className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Extend Session
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutWarning;
